import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Bookmark, Plus, Trash, MagnifyingGlass, FileText, Info } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function QuoteTemplatesPage() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await API.get("/quote-templates");
      const payload = res.data;
      setTemplates(Array.isArray(payload) ? payload : (payload?.templates ?? []));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load templates");
    }
    setLoading(false);
  };

  const handleDelete = async (templateId) => {
    try {
      await API.delete(`/quote-templates/${templateId}`);
      toast.success("Template deleted");
      setShowDeleteConfirm(null);
      fetchTemplates();
    } catch (err) {
      toast.error("Failed to delete template");
    }
  };

  const filteredTemplates = templates.filter(t => 
    !search || t.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
            <Bookmark className="w-7 h-7" weight="duotone" />
            Quote Templates
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Save and reuse quote configurations for faster quote creation
          </p>
        </div>

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" weight="duotone" />
              <div className="text-sm text-blue-900 dark:text-blue-100">
                <p className="font-medium mb-1">About Quote Templates</p>
                <p>
                  Templates save line items, markups, terms, and validity settings. Use the "Save as Template" button
                  when creating a quote to save your current configuration. Templates can be loaded when creating new quotes.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Templates List */}
        {filteredTemplates.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Bookmark className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" weight="duotone" />
              <p className="text-muted-foreground mb-2">No templates found</p>
              <p className="text-sm text-muted-foreground">
                Create a quote and use "Save as Template" to save reusable configurations
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map((template) => (
              <Card key={template._id} className="hover:shadow-md transition">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-base flex items-center gap-2">
                        <FileText className="w-4 h-4 text-primary" />
                        {template.name}
                      </CardTitle>
                      <CardDescription className="text-xs mt-1">
                        {template.line_items?.length || 0} line items
                      </CardDescription>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowDeleteConfirm(template)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
                    >
                      <Trash className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{template.markup_a_label}:</span>
                    <Badge variant="outline">{template.markup_a_percent}%</Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{template.markup_b_label}:</span>
                    <Badge variant="outline">{template.markup_b_percent}%</Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Validity:</span>
                    <Badge variant="outline">{template.validity_days} days</Badge>
                  </div>
                  {template.terms && (
                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {template.terms}
                      </p>
                    </div>
                  )}
                  <div className="pt-2 text-xs text-muted-foreground">
                    Created {new Date(template.created_at).toLocaleDateString()}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Usage Instructions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">How to Use Templates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p><strong>1. Save a Template:</strong> When creating a quote, fill in line items, markups, and terms. Click "Save as Template" before saving the quote.</p>
            <p><strong>2. Load a Template:</strong> When creating a new quote, select a template from the "Load Template" dropdown to populate the form.</p>
            <p><strong>3. Customize:</strong> After loading a template, you can modify line items, markups, or any other field before saving the quote.</p>
            <p><strong>4. Manage Templates:</strong> Delete templates you no longer need from this page.</p>
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={(open) => !open && setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the template "{showDeleteConfirm?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => handleDelete(showDeleteConfirm._id)}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
